import React from "react";
import { useFinanceConfig } from "../../context/FinanceConfigContext";

export interface CatalogItem {
  id: string;
  name: string;
  type: "asset" | "part" | "labor" | "travel" | "service" | "shipping" | "carriage";
  subtitle: string;
  category: string;
  basePrice: number;
  unit: string;
  packageTypeId?: string;
}

// Backward-compat alias
export type Asset = CatalogItem;

type TabType = "part" | "labor" | "travel" | "service" | "shipping" | "carriage";

const MOCK_ASSETS: CatalogItem[] = [
  { id: "a1",  name: "Asset Installation",              type: "asset", subtitle: "Full install, mount & commissioning",      category: "Installation",   basePrice: 1200,  unit: "each" },
  { id: "a2",  name: "Asset Decommissioning",           type: "asset", subtitle: "Safe removal & disposal",                  category: "Installation",   basePrice: 650,   unit: "each" },
  { id: "a3",  name: "Preventive Asset Maintenance",    type: "asset", subtitle: "Scheduled PM — full asset service",        category: "Maintenance",    basePrice: 480,   unit: "each" },
  { id: "a4",  name: "Corrective Asset Maintenance",    type: "asset", subtitle: "Unscheduled breakdown repair",             category: "Maintenance",    basePrice: 0,     unit: "hour" },
  { id: "a5",  name: "Asset Onsite Consultation",       type: "asset", subtitle: "Expert review at customer site",           category: "Consultation",   basePrice: 350,   unit: "visit" },
  { id: "a6",  name: "Asset Health Assessment",         type: "asset", subtitle: "Condition report & recommendations",       category: "Inspection",     basePrice: 280,   unit: "each" },
  { id: "a7",  name: "Asset Calibration",               type: "asset", subtitle: "Precision calibration with certificate",   category: "Calibration",    basePrice: 320,   unit: "each" },
  { id: "a8",  name: "Asset Relocation",                type: "asset", subtitle: "Move, reinstall & recommission on-site",   category: "Installation",   basePrice: 890,   unit: "each" },
  { id: "a9",  name: "Asset Software / Firmware Update",type: "asset", subtitle: "OEM-approved update & configuration",      category: "Software",       basePrice: 180,   unit: "each" },
  { id: "a10", name: "Asset Safety Inspection",         type: "asset", subtitle: "Compliance & safety certification check",  category: "Inspection",     basePrice: 240,   unit: "each" },
  { id: "a11", name: "Asset Performance Optimisation",  type: "asset", subtitle: "Tuning & efficiency improvement service",  category: "Maintenance",    basePrice: 420,   unit: "each" },
  { id: "a12", name: "Asset Warranty Support Visit",    type: "asset", subtitle: "In-warranty onsite diagnosis & fix",       category: "Consultation",   basePrice: 0,     unit: "visit" },
];

const MOCK_PARTS: CatalogItem[] = [
  { id: "p1", name: "Oil Filter — OEM Grade",      type: "part", subtitle: "PN-0410-231", category: "Filters",    basePrice: 28,  unit: "each" },
  { id: "p2", name: "Hydraulic Seal Kit",           type: "part", subtitle: "PN-0512-099", category: "Seals",      basePrice: 145, unit: "each" },
  { id: "p3", name: "Ball Bearing 6205-ZZ",         type: "part", subtitle: "PN-0621-044", category: "Bearings",   basePrice: 18,  unit: "each" },
  { id: "p4", name: "V-Belt A42 Industrial",        type: "part", subtitle: "PN-0710-117", category: "Belts",      basePrice: 34,  unit: "each" },
  { id: "p5", name: "Pressure Relief Valve",        type: "part", subtitle: "PN-0808-302", category: "Valves",     basePrice: 210, unit: "each" },
  { id: "p6", name: "Coolant Hose 32mm",            type: "part", subtitle: "PN-0915-058", category: "Hoses",      basePrice: 42,  unit: "each" },
  { id: "p7", name: "Air Filter Element",           type: "part", subtitle: "PN-1023-201", category: "Filters",    basePrice: 55,  unit: "each" },
  { id: "p8",  name: "Fuel Injector Nozzle",                        type: "part", subtitle: "PN-1130-441",   category: "Fuel System", basePrice: 380,    unit: "each" },
  { id: "p9",  name: "Grouper Lug 4X57/204 EXT LHM",              type: "part", subtitle: "U5008400S-001", category: "Mechanical",  basePrice: 482.79, unit: "each" },
  { id: "p10", name: "Tote Box LG Gray HDPE 19.5\"×15.5\"×10\"",  type: "part", subtitle: "U5006370P",     category: "Storage",     basePrice: 77.65,  unit: "each" },
  { id: "p11", name: "Tote Lid LG Gray HDPE 19.5\"×15.5\"",       type: "part", subtitle: "U5006372P",     category: "Storage",     basePrice: 42.36,  unit: "each" },
];

const MOCK_LABOR: CatalogItem[] = [
  { id: "l1", name: "Preventive Maintenance",        type: "labor", subtitle: "Scheduled PM service",          category: "Maintenance",  basePrice: 0, unit: "hour", packageTypeId: "pt1" },
  { id: "l2", name: "Corrective Repair",             type: "labor", subtitle: "Unscheduled breakdown repair",  category: "Repair",       basePrice: 0, unit: "hour", packageTypeId: "pt1" },
  { id: "l3", name: "Installation & Commissioning",  type: "labor", subtitle: "New equipment setup",           category: "Installation", basePrice: 0, unit: "hour", packageTypeId: "pt1" },
  { id: "l4", name: "Inspection & Diagnostics",      type: "labor", subtitle: "Technical assessment",          category: "Inspection",   basePrice: 0, unit: "hour", packageTypeId: "pt1" },
  { id: "l5", name: "Calibration Service",           type: "labor", subtitle: "Equipment calibration",         category: "Calibration",  basePrice: 0, unit: "hour", packageTypeId: "pt1" },
  { id: "l6", name: "Software Update / Upgrade",     type: "labor", subtitle: "Firmware and software update",  category: "Software",     basePrice: 0, unit: "hour", packageTypeId: "pt1" },
];

const MOCK_TRAVEL: CatalogItem[] = [
  { id: "t1", name: "On-Site Visit",            type: "travel", subtitle: "Standard on-site service call",  category: "Site Visit", basePrice: 0, unit: "trip", packageTypeId: "pt3" },
  { id: "t2", name: "Emergency Call-Out",       type: "travel", subtitle: "Urgent after-hours visit",       category: "Emergency",  basePrice: 0, unit: "trip", packageTypeId: "pt3" },
  { id: "t3", name: "Remote Support Session",   type: "travel", subtitle: "Telephone / video support",      category: "Remote",     basePrice: 0, unit: "trip", packageTypeId: "pt3" },
  { id: "t4", name: "Travel Allowance",         type: "travel", subtitle: "Per diem travel expense",        category: "Allowance",  basePrice: 0, unit: "day",  packageTypeId: "pt3" },
  { id: "t5", name: "Mileage Reimbursement",    type: "travel", subtitle: "Distance-based travel cost",     category: "Allowance",  basePrice: 0, unit: "trip", packageTypeId: "pt3" },
];

const MOCK_SHIPPING: CatalogItem[] = [
  { id: "sh1", name: "Standard Shipping",       type: "shipping", subtitle: "3–5 business days",               category: "Standard",      basePrice: 12,  unit: "shipment", packageTypeId: "pt4" },
  { id: "sh2", name: "Express Shipping",        type: "shipping", subtitle: "1–2 business days",               category: "Express",       basePrice: 28,  unit: "shipment", packageTypeId: "pt4" },
  { id: "sh3", name: "Overnight Shipping",      type: "shipping", subtitle: "Next business day delivery",      category: "Express",       basePrice: 55,  unit: "shipment", packageTypeId: "pt4" },
  { id: "sh4", name: "International Shipping",  type: "shipping", subtitle: "7–14 business days, tracked",     category: "International", basePrice: 45,  unit: "shipment", packageTypeId: "pt4" },
  { id: "sh5", name: "Freight Shipping",        type: "shipping", subtitle: "Heavy / oversized items",         category: "Freight",       basePrice: 0,   unit: "shipment", packageTypeId: "pt4" },
  { id: "sh6", name: "Same-Day Delivery",       type: "shipping", subtitle: "Local same-day courier",          category: "Express",       basePrice: 85,  unit: "shipment", packageTypeId: "pt4" },
];

const MOCK_CARRIAGE: CatalogItem[] = [
  { id: "cr1", name: "Local Carriage",          type: "carriage", subtitle: "Delivery within 30 km radius",    category: "Local",         basePrice: 35,  unit: "delivery", packageTypeId: "pt5" },
  { id: "cr2", name: "Regional Freight",        type: "carriage", subtitle: "Palletised goods, regional zone", category: "Regional",      basePrice: 110, unit: "delivery", packageTypeId: "pt5" },
  { id: "cr3", name: "Pallet Delivery",         type: "carriage", subtitle: "Euro pallet, tail-lift service",  category: "Freight",       basePrice: 95,  unit: "pallet",   packageTypeId: "pt5" },
  { id: "cr4", name: "Courier Service",         type: "carriage", subtitle: "Door-to-door tracked courier",    category: "Courier",       basePrice: 22,  unit: "delivery", packageTypeId: "pt5" },
  { id: "cr5", name: "Haulage — Full Load",     type: "carriage", subtitle: "Dedicated full vehicle load",     category: "Haulage",       basePrice: 0,   unit: "load",     packageTypeId: "pt5" },
  { id: "cr6", name: "Haulage — Part Load",     type: "carriage", subtitle: "Shared vehicle, part load",       category: "Haulage",       basePrice: 0,   unit: "load",     packageTypeId: "pt5" },
];

const MOCK_SERVICE_PACKAGES: CatalogItem[] = [
  { id: "sp1",  name: "Basic Support Package",          type: "service", subtitle: "Email & phone support, 5×8, 4h response SLA",           category: "Support",       basePrice: 299,   unit: "month",   packageTypeId: "pt2" },
  { id: "sp2",  name: "Premium Support Package",        type: "service", subtitle: "Priority support, 24×7, 2h response SLA",               category: "Support",       basePrice: 799,   unit: "month",   packageTypeId: "pt2" },
  { id: "sp3",  name: "Enterprise Support Package",     type: "service", subtitle: "Dedicated engineer, 24×7, 1h response + onsite SLA",    category: "Support",       basePrice: 1999,  unit: "month",   packageTypeId: "pt2" },
  { id: "sp4",  name: "Annual Maintenance Contract",    type: "service", subtitle: "12× scheduled PM visits + parts included",              category: "Maintenance",   basePrice: 3600,  unit: "year",    packageTypeId: "pt2" },
  { id: "sp5",  name: "Quarterly Maintenance Package",  type: "service", subtitle: "4× PM visits + inspection report per quarter",          category: "Maintenance",   basePrice: 980,   unit: "quarter", packageTypeId: "pt1" },
  { id: "sp6",  name: "Full-Coverage Service Plan",     type: "service", subtitle: "Unlimited callouts, parts & labor all-inclusive",       category: "Maintenance",   basePrice: 5500,  unit: "year",    packageTypeId: "pt2" },
  { id: "sp7",  name: "Remote Monitoring Package",      type: "service", subtitle: "24×7 IoT monitoring, alerts & monthly report",         category: "Monitoring",    basePrice: 149,   unit: "month",   packageTypeId: "pt1" },
  { id: "sp8",  name: "Onsite Consultation Block",      type: "service", subtitle: "Block of 10 expert site visits, valid 12 months",      category: "Consultation",  basePrice: 2800,  unit: "block",   packageTypeId: "pt3" },
  { id: "sp9",  name: "Training Package — Operators",   type: "service", subtitle: "On-site operator training, up to 10 staff",            category: "Training",      basePrice: 1200,  unit: "session", packageTypeId: "pt1" },
  { id: "sp10", name: "Training Package — Technicians", type: "service", subtitle: "Advanced technician certification programme",          category: "Training",      basePrice: 2400,  unit: "session", packageTypeId: "pt1" },
  { id: "sp11", name: "Spare Parts Retainer",           type: "service", subtitle: "Pre-paid parts credit, 10% discount on all orders",    category: "Parts",         basePrice: 1000,  unit: "year",    packageTypeId: "pt2" },
  { id: "sp12", name: "Commissioning Package",          type: "service", subtitle: "Site survey, install, config & operator handover",     category: "Installation",  basePrice: 4200,  unit: "each",    packageTypeId: "pt3" },
];

const ALL_ITEMS: Record<TabType, CatalogItem[]> = {
  service:  MOCK_SERVICE_PACKAGES,
  part:     MOCK_PARTS,
  labor:    MOCK_LABOR,
  travel:   MOCK_TRAVEL,
  shipping: MOCK_SHIPPING,
  carriage: MOCK_CARRIAGE,
};

const TAB_LABELS: Record<TabType, string> = {
  service:  "Service Packages",
  part:     "Parts",
  labor:    "Labor",
  travel:   "Travel",
  shipping: "Shipping",
  carriage: "Carriage",
};

export interface CatalogContract {
  laborRate: number;
  partsDiscountRate: number;
  travelRate: number;
}

interface CatalogModalProps {
  onClose: () => void;
  onAdd: (item: CatalogItem) => void;
  addedItemIds?: Set<string>;
  contract?: CatalogContract | null;
}

// Backward-compat wrapper (QuotationBuilderPage currently imports AssetModal)
interface AssetModalProps {
  onClose: () => void;
  onAdd: (asset: CatalogItem) => void;
  addedAssetIds?: Set<string>;
  contract?: CatalogContract | null;
}

export function AssetModal({ onClose, onAdd, addedAssetIds = new Set(), contract }: AssetModalProps) {
  return <CatalogModal onClose={onClose} onAdd={onAdd} addedItemIds={addedAssetIds} contract={contract} />;
}

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="var(--color-content-tertiary)" strokeWidth="1.5" strokeLinecap="round">
    <circle cx="7" cy="7" r="4.5" />
    <path d="M10.5 10.5l3 3" />
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M5 5l10 10M15 5L5 15" />
  </svg>
);

const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M7 2v10M2 7h10" />
  </svg>
);

const STATIC_TABS: TabType[] = ["part"];

const ALL_PACKAGE_TYPE_ITEMS: CatalogItem[] = [
  ...MOCK_SERVICE_PACKAGES,
  ...MOCK_LABOR,
  ...MOCK_TRAVEL,
  ...MOCK_SHIPPING,
  ...MOCK_CARRIAGE,
];

export function CatalogModal({ onClose, onAdd, addedItemIds = new Set(), contract }: CatalogModalProps) {
  const { config: financeConfig } = useFinanceConfig();
  const packageTypes = financeConfig.packageTypes;
  const defaultTab = packageTypes[0]?.id ?? "part";
  const [activeTab, setActiveTab] = React.useState<string>(defaultTab);
  const [search, setSearch] = React.useState("");

  const isPackageTypeTab = packageTypes.some(pt => pt.id === activeTab);
  const items: CatalogItem[] = isPackageTypeTab
    ? ALL_PACKAGE_TYPE_ITEMS.filter(item => item.packageTypeId === activeTab)
    : ALL_ITEMS[activeTab as TabType] ?? [];

  const filtered = items.filter((item) => {
    const q = search.toLowerCase();
    return item.name.toLowerCase().includes(q) || item.subtitle.toLowerCase().includes(q);
  });

  function handleTabChange(tab: string) {
    setActiveTab(tab);
    setSearch("");
  }

  function getPriceLabel(item: CatalogItem): string {
    if (item.type === "labor") {
      return contract ? `€ ${contract.laborRate}/hr` : "—";
    }
    if (item.type === "travel") {
      const unit = item.unit === "day" ? "day" : "trip";
      return contract ? `€ ${contract.travelRate}/${unit}` : "—";
    }
    if (item.basePrice === 0 && item.type === "asset") return "Rate from contract";
    return `€ ${item.basePrice.toLocaleString()} / ${item.unit}`;
  }

  function getDiscountTag(item: CatalogItem): string | null {
    if (item.type === "part" && contract && contract.partsDiscountRate > 0) {
      return `-${contract.partsDiscountRate}%`;
    }
    return null;
  }

  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(23,43,77,0.4)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const modalStyle: React.CSSProperties = {
    background: "var(--color-bg-primary)",
    borderRadius: "var(--radius-m)",
    boxShadow: "var(--elevation-xl)",
    width: 700,
    maxWidth: "calc(100vw - 48px)",
    maxHeight: "80vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  };

  const itemCount = filtered.length;
  const itemLabel = isPackageTypeTab ? "package" : activeTab === "part" ? "part" : "item";

  return (
    <div style={overlayStyle} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modalStyle} role="dialog" aria-modal="true" aria-label="Browse Items">

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "var(--spacing-l) var(--spacing-2xl)", borderBottom: "1px solid var(--color-border-primary)", flexShrink: 0 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "var(--color-content-primary)", letterSpacing: "-0.32px" }}>
              Browse Items
            </h2>
            <p style={{ margin: "2px 0 0", fontSize: 12, fontWeight: 500, color: "var(--color-content-secondary)" }}>
              Select service packages, parts, labor, travel, shipping, or carriage to add as line items
            </p>
          </div>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-content-secondary)", display: "flex", alignItems: "center", padding: "var(--spacing-2xs)", borderRadius: "var(--radius-s)" }}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", borderBottom: "1px solid var(--color-border-primary)", flexShrink: 0, padding: "0 var(--spacing-2xl)", overflowX: "auto" }}>
          {/* Dynamic package type tabs */}
          {packageTypes.map(pt => (
            <button
              key={pt.id}
              onClick={() => handleTabChange(pt.id)}
              style={{
                background: "none", border: "none",
                borderBottom: activeTab === pt.id ? "2px solid var(--color-content-brand)" : "2px solid transparent",
                padding: "10px 14px", marginBottom: -1, cursor: "pointer",
                fontFamily: "var(--font-family-base)", fontSize: 13, whiteSpace: "nowrap",
                fontWeight: activeTab === pt.id ? 700 : 500,
                color: activeTab === pt.id ? "var(--color-content-brand)" : "var(--color-content-secondary)",
                transition: "all 120ms ease",
              }}
            >
              {pt.name}
            </button>
          ))}
          {/* Static tabs */}
          {STATIC_TABS.map(tab => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              style={{
                background: "none", border: "none",
                borderBottom: activeTab === tab ? "2px solid var(--color-content-brand)" : "2px solid transparent",
                padding: "10px 14px", marginBottom: -1, cursor: "pointer",
                fontFamily: "var(--font-family-base)", fontSize: 13, whiteSpace: "nowrap",
                fontWeight: activeTab === tab ? 700 : 500,
                color: activeTab === tab ? "var(--color-content-brand)" : "var(--color-content-secondary)",
                transition: "all 120ms ease",
              }}
            >
              {TAB_LABELS[tab]}
            </button>
          ))}
        </div>

        {/* Search + category filter */}
        <div style={{ padding: "var(--spacing-l) var(--spacing-2xl)", borderBottom: "1px solid var(--color-border-primary)", display: "flex", flexDirection: "column", gap: "var(--spacing-s)", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--spacing-s)", background: "var(--color-bg-accent)", border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-s)", padding: "var(--spacing-s) var(--spacing-m)" }}>
            <SearchIcon />
            <input
              style={{ flex: 1, border: "none", background: "transparent", fontFamily: "var(--font-family-base)", fontSize: 14, fontWeight: 500, color: "var(--color-content-primary)", outline: "none" }}
              placeholder={isPackageTypeTab ? "Search packages…" : activeTab === "part" ? "Search by name or part number…" : "Search by name or description…"}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          {activeTab === "part" && contract && contract.partsDiscountRate > 0 && (
            <div style={{ fontSize: 12, fontWeight: 500, color: "var(--color-content-brand)", background: "var(--color-bg-brand-lightest)", border: "1px solid var(--color-border-brand)", borderRadius: "var(--radius-s)", padding: "6px 10px" }}>
              Contract parts discount: {contract.partsDiscountRate}% — applied automatically when added
            </div>
          )}
        </div>

        {/* Item list */}
        <div style={{ flex: 1, overflowY: "auto", padding: "var(--spacing-s) var(--spacing-2xl)" }}>
          {filtered.length === 0 ? (
            <div style={{ textAlign: "center", padding: "var(--spacing-3xl) 0", color: "var(--color-content-secondary)", fontSize: 14 }}>
              No {itemLabel}s found
            </div>
          ) : (
            filtered.map((item) => {
              const isAdded = addedItemIds.has(item.id);
              const discountTag = getDiscountTag(item);
              return (
                <div
                  key={item.id}
                  style={{ display: "flex", alignItems: "center", gap: "var(--spacing-m)", padding: "var(--spacing-m) 0", borderBottom: "1px solid var(--color-border-primary)" }}
                >
                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "var(--color-content-primary)", marginBottom: 2, letterSpacing: "-0.14px" }}>
                      {item.name}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 500, color: "var(--color-content-secondary)" }}>
                      {item.subtitle}{activeTab === "service" && ` · ${item.category}`}
                    </div>
                  </div>

                  {/* Price + discount tag */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0, minWidth: 100, justifyContent: "flex-end" }}>
                    {discountTag && (
                      <span style={{ fontSize: 11, fontWeight: 700, background: "#D1FAE5", color: "#059669", borderRadius: 4, padding: "1px 5px", fontFamily: "var(--font-family-base)" }}>
                        {discountTag}
                      </span>
                    )}
                    <span style={{ fontSize: 14, fontWeight: 700, color: "var(--color-content-primary)", letterSpacing: "-0.14px" }}>
                      {getPriceLabel(item)}
                    </span>
                  </div>

                  {/* Add button */}
                  <button
                    onClick={() => !isAdded && onAdd(item)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "6px 12px",
                      border: "1px solid",
                      borderColor: isAdded ? "var(--color-border-primary)" : "var(--color-border-brand)",
                      borderRadius: "var(--radius-s)",
                      background: "transparent",
                      color: isAdded ? "var(--color-content-secondary)" : "var(--color-content-brand)",
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: isAdded ? "default" : "pointer",
                      fontFamily: "var(--font-family-base)",
                      flexShrink: 0,
                      transition: "all 120ms ease",
                    }}
                  >
                    {!isAdded && <PlusIcon />}
                    {isAdded ? "Added" : "Add"}
                  </button>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "var(--spacing-m) var(--spacing-2xl)", borderTop: "1px solid var(--color-border-primary)", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <span style={{ fontSize: 12, color: "var(--color-content-secondary)", fontWeight: 500 }}>
            {itemCount} {itemLabel}{itemCount !== 1 ? "s" : ""} shown
          </span>
          <button
            onClick={onClose}
            style={{ padding: "var(--spacing-s) var(--spacing-l)", border: "1px solid var(--color-border-primary)", borderRadius: "var(--radius-s)", background: "var(--color-bg-primary)", color: "var(--color-content-primary)", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "var(--font-family-base)" }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
